import { useTranslation } from "react-i18next";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { Group } from "@dashboard/devices/types";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { useAssignPolicyToGroupsMutation, useUnassignPolicyFromGroupsMutation } from "@dashboard/policies/api";
import { FilterTreeFormValues, FilterTreeModal } from "@dashboard/shared/components/GroupFilterHeader/FilterTreeModal";
import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { Theme } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { showSuccessToast } from "@shared/toasts/Toasts";

function hasConflictingGroup(checkedGroupIds: number[], allGroups: Group[], policyId: string) {
    return allGroups.some(
        (group) => checkedGroupIds.includes(group.id) && group.policyId !== null && group.policyId !== policyId,
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    warning: {
        marginBottom: theme.spacing(2),
    },
}));

type Props = {
    open: boolean;
    onClose: () => void;
    policy: API.PolicyViewModel;
};

export const AssignGroupsModal = (props: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["modals", "common"]);

    const allGroups = useSelector(selectGroups);
    const { data: workspace = null } = useCurrentWorkspace();
    const { groupAbility } = PermissionService();
    const isGroupDisabled = (group: Group) => groupAbility(group, workspace).cannot("assign", "Policy");

    const [assignPolicyToGroups, { isLoading: isAssigning }] = useAssignPolicyToGroupsMutation();
    const [unassignPolicyFromGroups, { isLoading: isUnassigning }] = useUnassignPolicyFromGroupsMutation();

    const initialValues: FilterTreeFormValues = Object.fromEntries(
        allGroups.map((group) => [`$${group.id}`, props.policy.groupIds?.includes(group.id) ?? false]),
    );

    const onSubmit = async (values: FilterTreeFormValues) => {
        const checkedGroupIds = Object.entries(values)
            .filter(([, checked]) => checked)
            .map(([id]) => Number(id));
        const previouslyAssignedGroupIds = props.policy.groupIds;

        const groupIdsToAssign = checkedGroupIds.filter((id) => !previouslyAssignedGroupIds?.includes(id));
        const groupIdsToUnassign = previouslyAssignedGroupIds?.filter((id) => !checkedGroupIds.includes(id));

        const hasGroupsToAssign = groupIdsToAssign.length > 0;
        const hasGroupsToUnassign = groupIdsToUnassign && groupIdsToUnassign.length > 0;

        if (!hasGroupsToAssign && !hasGroupsToUnassign) {
            return;
        }

        if (hasGroupsToAssign) {
            const result = await assignPolicyToGroups({ id: props.policy.id, groupIds: groupIdsToAssign });
            if ("error" in result) {
                return false;
            }
        }

        if (hasGroupsToUnassign) {
            const result = await unassignPolicyFromGroups({ id: props.policy.id, groupIds: groupIdsToUnassign });
            if ("error" in result) {
                return false;
            }
        }

        showSuccessToast(t("modals:assignPolicyToGroups.toast.success"));
    };

    return (
        <FilterTreeModal
            open={props.open}
            initialValues={initialValues}
            onClose={props.onClose}
            onSubmit={onSubmit}
            title={t("modals:assignPolicyToGroups.title")}
            description={t("modals:assignPolicyToGroups.description")}
            confirmLabel={t("modals:assignPolicyToGroups.button")}
            confirmLoading={isAssigning || isUnassigning}
            isGroupDisabled={isGroupDisabled}
            disabledTooltip={t("common:tooltip.cannotManagePolicies")}
            banner={(checkedGroupIds) => {
                const showWarning = hasConflictingGroup(checkedGroupIds, allGroups, props.policy.id);
                return showWarning ? (
                    <CustomAlert severity="warning" className={classes.warning}>
                        {t("modals:assignPolicyToGroups.warning")}
                    </CustomAlert>
                ) : null;
            }}
        />
    );
};
